import { InputChangeEventDetail } from '@wppopen/components-library'
import { WppIconCross, WppIconSearch, WppInput } from '@wppopen/components-library-react'
import { WppInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import { FilterReducerAction, InputFilterComponentData } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { addFreetextFilter, replaceFreetextFilter } from 'reducers/GenericFiltersReducer/GenericFiltersReducer'
import React, { useState, KeyboardEvent, useEffect, useCallback } from 'react'
import styles from './Filters.module.scss'
import clsx from 'clsx'

interface FilterInputProps {
  filterObject: InputFilterComponentData
  reducerDispatch: React.Dispatch<FilterReducerAction>
  isLoading: boolean
  isGroupItem?: boolean
  className?: string
}

function FilterInput({ filterObject, reducerDispatch, isLoading, isGroupItem = false, className }: FilterInputProps) {
  const { id, labelText, placeholder, activeFilters, isMultiSelect } = filterObject

  const typeSafeActiveFilters = activeFilters as string[]

  const [inputValue, setInputValue] = useState<string>(!isMultiSelect ? activeFilters[0] || '' : '')
  const [prevSearchTerm, setPrevSearchTerm] = useState<string>(typeSafeActiveFilters[0])
  const [hasCanceled, setHasCanceled] = useState<boolean>(false)
  const handleChange = (event: WppInputCustomEvent<InputChangeEventDetail>) => {
    const { value } = event.target
    setInputValue(value)
  }

  const appendFilter = useCallback(() => {
    if (isMultiSelect) {
      if (!inputValue) return
      reducerDispatch(addFreetextFilter({ filterObject, newFilterValue: inputValue }))
      setInputValue('')
    } else {
      if (inputValue === prevSearchTerm) return
      reducerDispatch(replaceFreetextFilter({ filterObject, newFilterValue: inputValue }))
      setPrevSearchTerm(inputValue)
    }
  }, [filterObject, inputValue, isMultiSelect, prevSearchTerm, reducerDispatch])

  const handleKeyDown = (event: KeyboardEvent<HTMLWppInputElement>) => {
    const { key } = event
    if (key === 'Enter') {
      appendFilter()
    }
  }

  const handleBlur = () => {
    if (!isMultiSelect && inputValue === '') {
      appendFilter()
    }
  }

  const handleCancel = () => {
    setInputValue('')
    setHasCanceled(true)
  }

  useEffect(() => {
    if (!hasCanceled || inputValue !== '') return
    appendFilter()
    setHasCanceled(false)
  }, [inputValue, hasCanceled, appendFilter, setHasCanceled])

  // used for prefilling the input with the active filter value when navigating back to the page
  useEffect(() => {
    if (!isMultiSelect) setInputValue(activeFilters[0] || '')
  }, [activeFilters, isMultiSelect])

  const hasActiveFilters = isMultiSelect && typeSafeActiveFilters.length
  const activeFilterCount = hasActiveFilters ? ` (${typeSafeActiveFilters.length})` : ''

  return (
    <WppInput
      name={id}
      className={clsx(styles.filter__input, className)}
      labelConfig={{
        text: isGroupItem ? '' : `${labelText}${activeFilterCount}`,
      }}
      required
      value={inputValue}
      placeholder={isGroupItem ? 'Search' : placeholder || 'Start typing ...'}
      onWppChange={handleChange}
      onKeyDown={handleKeyDown}
      onWppBlur={handleBlur}
      data-testid={`${id}-input`}
      disabled={isLoading}
      type="text"
    >
      {!!inputValue && <WppIconCross slot="icon-end" onClick={handleCancel} />}{' '}
      <WppIconSearch slot="icon-start" className={styles.searchIcon} onClick={() => appendFilter()} />{' '}
    </WppInput>
  )
}

export default FilterInput
